<!--
  TrustBar — barra horizontal de credibilidade com pequenos ícones
  e textos curtos (ex: "+10 anos no mercado", "+12.000 profissionais").

  Props:
    - items : Array<{ texto: string, icone?: 'shield' | 'clock' | 'users' | VNode }>
              ou pode passar via slot default.

  Uso rápido (itens padrão com ícones nativos):
    <TrustBar :items="[
      { texto: 'Registrado e homologado', icone: 'shield' },
      { texto: '+10 anos no mercado', icone: 'clock' },
      { texto: '+12.000 profissionais', icone: 'users' }
    ]" />

  Uso customizado (ícones livres):
    <TrustBar>
      <div class="trust-item"><svg/>...</svg><span>...</span></div>
    </TrustBar>
-->
<template>
  <div class="trust-bar">
    <slot>
      <div v-for="(item, i) in items" :key="i" class="trust-item">
        <component :is="iconFor(item.icone)" />
        <span>{{ item.texto }}</span>
      </div>
    </slot>
  </div>
</template>

<script setup>
import { h } from 'vue'

defineProps({
  items: { type: Array, default: () => [] }
})

const svg = (paths) => () => h(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '16',
    height: '16',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
  },
  paths.map(d => h('path', { d }))
)

const ICONS = {
  shield: svg(['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z']),
  clock: () => h(
    'svg',
    { xmlns: 'http://www.w3.org/2000/svg', width: '16', height: '16', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
    [h('circle', { cx: '12', cy: '12', r: '10' }), h('polyline', { points: '12 6 12 12 16 14' })]
  ),
  users: () => h(
    'svg',
    { xmlns: 'http://www.w3.org/2000/svg', width: '16', height: '16', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' },
    [
      h('path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }),
      h('circle', { cx: '9', cy: '7', r: '4' }),
      h('path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }),
      h('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })
    ]
  )
}

const iconFor = (icone) => {
  if (!icone) return () => null
  if (typeof icone === 'string') return ICONS[icone] || (() => null)
  return icone
}
</script>

<style scoped>
.trust-bar {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--ds-s-6);
  margin-top: var(--ds-s-6);
  padding-top: var(--ds-s-6);
  border-top: 1px solid var(--ds-border-subtle);
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
}

.trust-item :deep(svg) {
  color: var(--ds-ciano);
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .trust-bar { gap: var(--ds-s-4); }
  .trust-item { font-size: 12px; }
}
</style>
